
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const ProductsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const categories = ['all', 'dental', 'diagnostic', 'reagents', 'equipment', 'supplies'];

  const products = [
    {
      id: 1,
      category: 'dental',
      image: 'https://public.readdy.ai/ai/img_res/525f7e05999cf7c3c15064c6fc9cff0e.jpg',
      name: 'Premium Ceramic Crowns',
      sku: 'DC-001',
      rating: 5,
      reviews: 128,
      description: 'High-quality ceramic crowns with natural aesthetics and superior durability for long-lasting dental restorations.',
      specifications: {
        'Material': 'Ceramic/Zirconia',
        'Turnaround': '3-5 days',
        'Warranty': 'Lifetime',
        'Compatibility': 'All systems',
      },
    },
    {
      id: 2,
      category: 'dental',
      image: 'https://readdy.ai/api/search-image?query=dental%20bridge%20prosthetic%20on%20clean%20white%20medical%20background%20professional%20product%20photography%20high%20quality%20detailed%20precision%20multiple%20teeth%20restoration&width=420&height=300&seq=prod2&orientation=landscape',
      name: 'Fixed Dental Bridges',
      sku: 'DB-002',
      rating: 5,
      reviews: 95,
      description: 'Custom-fabricated dental bridges using advanced CAD/CAM technology for precise fit and natural appearance.',
      specifications: {
        'Material': 'Porcelain/Metal',
        'Turnaround': '5-7 days',
        'Warranty': '10 years',
        'Units': '3-6 teeth',
      },
    },
    {
      id: 3,
      category: 'diagnostic',
      image: 'https://readdy.ai/api/search-image?query=medical%20rapid%20test%20kit%20diagnostic%20device%20on%20clean%20white%20laboratory%20background%20professional%20product%20photography%20modern%20healthcare%20technology%20high%20quality&width=420&height=300&seq=prod3&orientation=landscape',
      name: 'Rapid Test Kits',
      sku: 'RT-003',
      rating: 5,
      reviews: 210,
      description: 'Comprehensive rapid diagnostic test kits for quick and accurate point-of-care testing with easy-to-read results.',
      specifications: {
        'Test Time': '10-15 minutes',
        'Accuracy': '99.5%',
        'Storage': 'Room temperature',
        'Shelf Life': '24 months',
      },
    },
    {
      id: 4,
      category: 'diagnostic',
      image: 'https://readdy.ai/api/search-image?query=blood%20glucose%20testing%20meter%20device%20on%20clean%20white%20medical%20background%20professional%20product%20photography%20modern%20diabetes%20monitoring%20high%20quality&width=420&height=300&seq=prod4&orientation=landscape',
      name: 'Blood Glucose Monitors',
      sku: 'BG-004',
      rating: 5,
      reviews: 156,
      description: 'Advanced blood glucose monitoring systems with Bluetooth connectivity and comprehensive data tracking.',
      specifications: {
        'Test Time': '5 seconds',
        'Sample Size': '0.5 μL',
        'Memory': '500 results',
        'Connectivity': 'Bluetooth',
      },
    },
    {
      id: 5,
      category: 'reagents',
      image: 'https://readdy.ai/api/search-image?query=laboratory%20reagent%20bottles%20chemicals%20on%20clean%20white%20medical%20background%20professional%20product%20photography%20scientific%20supplies%20organized%20high%20quality&width=420&height=300&seq=prod5&orientation=landscape',
      name: 'Laboratory Reagents',
      sku: 'LR-005',
      rating: 5,
      reviews: 89,
      description: 'Premium quality laboratory reagents and chemicals for accurate testing and research applications.',
      specifications: {
        'Purity': '99.9%',
        'Volume': '500mL',
        'Storage': '2-8°C',
        'Certification': 'ISO certified',
      },
    },
    {
      id: 6,
      category: 'reagents',
      image: 'https://readdy.ai/api/search-image?query=diagnostic%20assay%20kit%20reagents%20on%20clean%20white%20laboratory%20background%20professional%20product%20photography%20medical%20testing%20supplies%20high%20quality%20organized&width=420&height=300&seq=prod6&orientation=landscape',
      name: 'Diagnostic Assay Kits',
      sku: 'DA-006',
      rating: 5,
      reviews: 142,
      description: 'Complete diagnostic assay kits with all necessary reagents and controls for reliable testing.',
      specifications: {
        'Tests per Kit': '100',
        'Sensitivity': '99.8%',
        'Specificity': '99.5%',
        'Shelf Life': '18 months',
      },
    },
    {
      id: 7,
      category: 'equipment',
      image: 'https://readdy.ai/api/search-image?query=laboratory%20centrifuge%20machine%20on%20clean%20white%20medical%20background%20professional%20product%20photography%20modern%20scientific%20equipment%20high%20quality&width=420&height=300&seq=prod7&orientation=landscape',
      name: 'Laboratory Centrifuge',
      sku: 'LC-007',
      rating: 5,
      reviews: 67,
      description: 'High-performance laboratory centrifuge with digital controls and multiple rotor options.',
      specifications: {
        'Max Speed': '15,000 RPM',
        'Capacity': '24 tubes',
        'Timer': '1-99 minutes',
        'Noise Level': 'Low',
      },
    },
    {
      id: 8,
      category: 'equipment',
      image: 'https://readdy.ai/api/search-image?query=digital%20microscope%20laboratory%20equipment%20on%20clean%20white%20medical%20background%20professional%20product%20photography%20modern%20scientific%20instrument%20high%20quality&width=420&height=300&seq=prod8&orientation=landscape',
      name: 'Digital Microscope',
      sku: 'DM-008',
      rating: 5,
      reviews: 93,
      description: 'Advanced digital microscope with high-resolution imaging and computer connectivity.',
      specifications: {
        'Magnification': '40x-1000x',
        'Camera': '5MP',
        'Display': 'LCD screen',
        'Software': 'Included',
      },
    },
    {
      id: 9,
      category: 'supplies',
      image: 'https://readdy.ai/api/search-image?query=medical%20laboratory%20gloves%20and%20supplies%20on%20clean%20white%20background%20professional%20product%20photography%20healthcare%20safety%20equipment%20organized%20high%20quality&width=420&height=300&seq=prod9&orientation=landscape',
      name: 'Laboratory Supplies Kit',
      sku: 'LS-009',
      rating: 5,
      reviews: 178,
      description: 'Comprehensive laboratory supplies including gloves, pipettes, tubes, and essential consumables.',
      specifications: {
        'Items': '50+ pieces',
        'Material': 'Medical grade',
        'Sterile': 'Yes',
        'Packaging': 'Individual',
      },
    },
  ];

  const filteredProducts = products
    .filter(product => 
      (selectedCategory === 'all' || product.category === selectedCategory) &&
      (searchQuery === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=medical%20laboratory%20products%20equipment%20and%20supplies%20display%20clean%20professional%20healthcare%20facility%20modern%20organized%20high%20quality&width=1920&height=800&seq=products-hero&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Our Products</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Premium laboratory products and equipment for healthcare professionals
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Products</span>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="sticky top-20 z-30 bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none text-sm cursor-pointer"
            >
              <option value="all">All Categories</option>
              {categories.filter(c => c !== 'all').map(category => (
                <option key={category} value={category} className="capitalize">
                  {category}
                </option>
              ))}
            </select>

            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none text-sm cursor-pointer"
            >
              <option value="name">Sort by Name</option>
              <option value="rating">Sort by Rating</option>
            </select>

            {/* View Mode */}
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'grid' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-label="Grid view"
              >
                <i className="ri-grid-line"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'list' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-label="List view"
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-8">
            <h2 className="text-2xl font-display font-bold text-gray-900">
              {filteredProducts.length} Product{filteredProducts.length !== 1 ? 's' : ''} Found
            </h2>
          </div>

          <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-8' : 'space-y-6'}>
            {filteredProducts.map((product, index) => (
              <AnimatedSection key={product.id} delay={index * 0.05}>
                {viewMode === 'grid' ? (
                  <div className="group bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300" data-product-shop>
                    <div className="relative h-72 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-primary-600 rounded-full text-sm font-semibold capitalize">
                          {product.category}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4 flex items-center space-x-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                        <i className="ri-star-fill text-yellow-400 text-sm"></i>
                        <span className="text-sm font-semibold text-gray-900">{product.rating}</span>
                        <span className="text-xs text-gray-600">({product.reviews})</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="text-sm text-gray-500 mb-2">SKU: {product.sku}</div>
                      <h3 className="text-xl font-display font-bold text-gray-900 mb-3">{product.name}</h3>
                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2">{product.description}</p>
                      <div className="flex space-x-3">
                        <Link
                          to="/quote"
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold text-center hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap"
                        >
                          Request Quote
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 flex" data-product-shop>
                    <div className="relative w-64 h-48 flex-shrink-0 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 p-6 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold capitalize">
                            {product.category}
                          </span>
                          <div className="flex items-center space-x-1">
                            <i className="ri-star-fill text-yellow-400"></i>
                            <span className="font-semibold text-gray-900">{product.rating}</span>
                            <span className="text-sm text-gray-600">({product.reviews} reviews)</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mb-2">SKU: {product.sku}</div>
                        <h3 className="text-2xl font-display font-bold text-gray-900 mb-3">{product.name}</h3>
                        <p className="text-gray-600 leading-relaxed">{product.description}</p>
                      </div>
                      <div className="flex space-x-3 mt-4">
                        <Link
                          to="/quote"
                          className="px-8 py-3 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap"
                        >
                          Request Quote
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <AnimatedSection className="py-24 bg-gradient-to-r from-primary-600 to-royal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Need Custom Solutions?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Contact us for customized products and bulk orders
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/quote"
              className="px-10 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-cyan-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              Request Custom Quote
            </Link>
            <Link
              to="/contact"
              className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default ProductsPage;
